<template>
  <div class="dashboard-route-stats-section">
    <div class="route-stats-header-row">
      <div class="route-stats-title">{{ $t('dashboard.routeStatistics') }}</div>
    </div>
    <div class="route-stats-table-wrapper">
      <Skeleton
        v-if="routeStatsLoading"
        width="100%"
        height="220px"
        style="margin-bottom: 1rem"
      />
      <DataTable
        v-else
        :value="routeStats"
        class="p-datatable-sm route-stats-table"
        :scrollable="true"
        scrollHeight="350px"
        v-model:expandedRows="expandedRowsProxy"
        dataKey="routeId"
      >
        <Column expander style="width: 3rem" />
        <Column field="routeName" :header="$t('dashboard.routeName')" />
        <Column field="totalRides" :header="$t('dashboard.totalRides')" />
        <Column field="finishedRides" :header="$t('dashboard.finished')" />
        <Column field="notStartedRides" :header="$t('dashboard.notStarted')" />
        <Column field="inProgressRides" :header="$t('dashboard.inProgress')" />
        <Column field="cancelledRides" :header="$t('dashboard.cancelled')" />
        <Column field="totalBookings" :header="$t('dashboard.totalBookings')" />
        <Column field="cancelledBookings" :header="$t('dashboard.cancelledBookings')" />
        <template #expansion="slotProps">
          <div class="route-rides-expansion">
            <div class="expansion-header">
              <h4>
                {{ $t('dashboard.rideDetailsFor') }} {{ slotProps.data.routeName }}
                <span
                  v-if="getRouteRides(slotProps.data.routeId)?.length"
                  class="rides-count"
                >
                  ({{ getRouteRides(slotProps.data.routeId).length }} {{ $t('dashboard.rides') }})
                </span>
              </h4>
            </div>
            <div
              v-if="getRouteRides(slotProps.data.routeId)?.length"
              class="route-rides-table"
            >
              <DataTable
                :value="getRouteRides(slotProps.data.routeId)"
                class="p-datatable-sm route-rides-table orange-header"
                :scrollable="true"
                scrollHeight="200px"
              >
                <Column field="rideId" :header="$t('dashboard.rideId')" />
                <Column field="rideDate" :header="$t('bookingDetails.rideDate')">
                  <template #body="slotProps">
                    {{ formatDate(slotProps.data.rideDate) }}
                  </template>
                </Column>
                <Column field="captainName" :header="$t('dashboard.captain')">
                  <template #body="slotProps">
                    {{ slotProps.data.captainName || $t('dashboard.notAssigned') }}
                  </template>
                </Column>
                <Column field="busType" :header="$t('dashboard.busType')">
                  <template #body="slotProps">
                    {{ slotProps.data.busType || $t('dashboard.notAssigned') }}
                  </template>
                </Column>
                <Column field="bookingsCount" :header="$t('dashboard.bookings')">
                  <template #body="slotProps">
                    <span class="bookings-count">{{
                      slotProps.data.bookingsCount
                    }}</span>
                  </template>
                </Column>
                <Column field="rideStatus" :header="$t('bookingDetails.status')">
                  <template #body="slotProps">
                    <Tag
                      :value="slotProps.data.rideStatus"
                      :severity="getStatusSeverity(slotProps.data.rideStatus)"
                    />
                  </template>
                </Column>
              </DataTable>
            </div>
            <div
              v-else-if="routeRidesLoading[slotProps.data.routeId]"
              class="route-rides-loading"
            >
              <div class="loading-content">
                <Skeleton width="100%" height="200px" />
                <p class="loading-text">{{ $t('dashboard.loadingRideDetails') }}</p>
              </div>
            </div>
            <div v-else class="no-route-rides">
              <i
                class="fa-solid fa-info-circle"
                style="color: #6b7280; margin-right: 0.5rem"
              ></i>
              {{ $t('dashboard.noRideDetails') }}
            </div>
          </div>
        </template>
      </DataTable>
      <div
        v-if="!routeStatsLoading && routeStats.length === 0"
        class="no-route-stats"
      >
        {{ $t('dashboard.noRouteStats') }}
      </div>
    </div>
  </div>
</template>
<script setup>
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Dropdown from "primevue/dropdown";
import Calendar from "primevue/calendar";
import Skeleton from "primevue/skeleton";
import Tag from "primevue/tag";
import { computed, defineProps, defineEmits, watch, ref } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
const props = defineProps({ filter: Object });
const { t } = useI18n();

const store = useStore();
const routeStatsLoading = ref(false);
const expandedRows = ref({});
const routeRidesLoading = ref({});

const expandedRowsProxy = computed({
  get: () => expandedRows.value,
  set: (v) => (expandedRows.value = v),
});

const routeStats = computed(
  () => store.getters["dashboard/getRouteStats"] || []
);
const getRouteRides = (routeId) =>
  store.getters["dashboard/getRouteRides"]?.[routeId] || [];

const loadRouteRides = async (routeId) => {
  if (routeRidesLoading.value[routeId]) return;
  routeRidesLoading.value[routeId] = true;
  let filter = { routeId };

  filter.day = props.filter.day;
  filter.from = props.filter.from;
  filter.to = props.filter.to;
  filter.type = props.filter.type;
  filter.corporateId = props.filter.corporateId;

  await store.dispatch("dashboard/fetchRouteRides", filter).finally(() => {
    routeRidesLoading.value[routeId] = false;
  });
};

const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};
const getStatusSeverity = (status) => {
  switch (status) {
    case t('dashboard.finished'):
      return "success";
    case t('dashboard.notStarted'):
      return "info";
    case t('dashboard.inProgress'):
      return "warning";
    case t('dashboard.cancelled'):
      return "danger";
    default:
      return "info";
  }
};
const fetchRouteStats = async () => {
  routeStatsLoading.value = true;

  await store
    .dispatch("dashboard/fetchRouteStats", props.filter)
    .finally(() => {
      routeStatsLoading.value = false;
    });
};

watch(() => props.filter, fetchRouteStats, {
  immediate: true,
  deep: true,
});

watch(
  expandedRows,
  (newExpandedRows) => {
    Object.keys(newExpandedRows).forEach((routeId) => {
      loadRouteRides(routeId);
    });
  },
  { deep: true }
);

watch(props.filter, () => {
  Object.keys(expandedRows.value).forEach((routeId) => {
    store.commit("dashboard/setRouteRides", { routeId, data: [] });
    loadRouteRides(routeId);
  });
});
</script>
<style scoped>
/* نسخنا نفس ستايلات الجدول من الملف الأصلي */
.dashboard-route-stats-section {
  margin-bottom: 1.5rem;
  background: #fff;
  border-radius: 1rem;
  box-shadow: 0 2px 8px 0 rgba(44, 62, 80, 0.07);
  padding: 2rem 1.5rem 1.5rem 1.5rem;
}
.route-stats-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}
.route-stats-title {
  font-size: 1.3rem;
  font-weight: 800;
  color: #014b8f;
  text-align: left;
  letter-spacing: 0.5px;
  margin-bottom: 0;
}
.route-stats-filter-row {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  margin-bottom: 0;
}
.filter-dropdown {
  min-width: 120px;
}
.filter-calendar {
  min-width: 140px;
}
.route-stats-table-wrapper {
  width: 100%;
  overflow-x: auto;
}
.route-stats-table {
  width: 100%;
  min-width: 800px;
}
:deep(.route-stats-table .p-datatable-thead > tr > th) {
  background-color: #014b8f !important;
  color: #fff !important;
  font-weight: 600 !important;
  border: none !important;
}
:deep(.route-stats-table .p-datatable-thead > tr > th:hover) {
}
:deep(.orange-header .p-datatable-thead > tr > th) {
  background-color: #fb923c !important;
  color: #fff !important;
  font-weight: 600 !important;
  border: none !important;
}
:deep(.orange-header .p-datatable-thead > tr > th:hover) {
}
.no-route-stats {
  text-align: center;
  color: #888;
  font-size: 1.1rem;
  padding: 1.5rem 0;
}
.route-rides-expansion {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 0.5rem;
  margin-top: 1rem;
}
.expansion-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}
.expansion-header h4 {
  margin-bottom: 0;
  color: #232946;
  font-size: 1.1rem;
  font-weight: 700;
}
.route-rides-table {
  width: 100%;
  min-width: 600px;
}
.route-rides-loading {
  width: 100%;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.loading-content {
  width: 100%;
  text-align: center;
}
.loading-text {
  margin-top: 1rem;
  color: #6b7280;
  font-size: 0.9rem;
  font-weight: 500;
}
.no-route-rides {
  text-align: center;
  color: #888;
  font-size: 1rem;
  padding: 1rem 0;
}
.bookings-count {
  font-weight: 600;
  color: #2563eb;
}
.route-rides-table .p-datatable {
  font-size: 0.9rem;
}
.route-rides-table .p-datatable .p-datatable-thead > tr > th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #232946;
}
.route-rides-table .p-datatable .p-datatable-tbody > tr > td {
  padding: 0.5rem;
}
.rides-count {
  font-size: 0.9rem;
  color: #6b7280;
  font-weight: 400;
}
@media (max-width: 900px) {
  .dashboard-route-stats-section {
    padding: 1rem 0.5rem;
  }
  .route-stats-table {
    min-width: 600px;
  }
}
</style>
